import React, { createContext, useContext, useMemo } from 'react';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { useUser } from './UserContext';
import {
    syncMealToSupabase, syncWorkoutToSupabase,
    loadMealsFromSupabase, loadWorkoutsFromSupabase,
    updateMealInSupabase, deleteMealFromSupabase,
    updateWorkoutInSupabase, deleteWorkoutFromSupabase
} from '../services/dataSyncService';
import { MIN_SAFE_CALORIES } from '../utils/productionSafety';

const LogContext = createContext();

export const useLog = () => useContext(LogContext);

export const LogProvider = ({ children }) => {
    const { userProfile } = useUser();

    // Daily Tracking - Persisted
    const [todaysMeals, setTodaysMeals] = useLocalStorage('calobite_meals', []);
    const [todaysExercises, setTodaysExercises] = useLocalStorage('calobite_exercises', []);
    const [waterIntake, setWaterIntake] = useLocalStorage('calobite_water', 0);
    const [stepsCount, setStepsCount] = useLocalStorage('calobite_steps', 0);
    const [dailyStreak, setDailyStreak] = useLocalStorage('calobite_streak', 1);

    // Reminders - Persisted
    const [reminders, setReminders] = useLocalStorage('calobite_reminders', [
        { id: 1, time: '08:00', type: 'meal', message: 'Time for breakfast! 🍳', enabled: true },
        { id: 2, time: '12:00', type: 'meal', message: 'Lunch time! 🥗', enabled: true },
        { id: 3, time: '15:00', type: 'exercise', message: 'Quick stretch break! 🧘', enabled: true },
        { id: 4, time: '18:00', type: 'meal', message: 'Dinner time! 🍽️', enabled: true },
        { id: 5, time: '21:00', type: 'water', message: 'Stay hydrated! 💧', enabled: true }
    ]);
    // Calorie Banking - Persisted
    const [calorieBank, setCalorieBank] = useLocalStorage('calobite_calorie_bank', 0);

    // Cloud Sync - Load from Supabase on mount/auth
    // This gives us "Supabase First" behavior while keeping LocalStorage as offline cache
    React.useEffect(() => {
        const loadCloudData = async () => {
            if (userProfile?.id) {
                try {
                    const today = new Date();
                    const [cloudMeals, cloudWorkouts] = await Promise.all([
                        loadMealsFromSupabase(today, userProfile.id),
                        loadWorkoutsFromSupabase(today, userProfile.id)
                    ]);

                    if (cloudMeals?.length > 0) setTodaysMeals(cloudMeals);
                    if (cloudWorkouts?.length > 0) setTodaysExercises(cloudWorkouts);
                    console.log(`[LogContext] Synced from cloud: ${cloudMeals.length} meals, ${cloudWorkouts.length} workouts`);
                } catch (err) {
                    console.warn('[LogContext] Cloud sync failed:', err);
                }
            }
        };
        loadCloudData();
    }, [userProfile?.id]);

    // Data is synced TO Supabase on each log operation for backup/persistence
    const dailySummary = useMemo(() => {
        const burned = todaysExercises.reduce((sum, ex) => sum + (ex.caloriesBurned || 0), 0);
        const protein = todaysMeals.reduce((sum, meal) => sum + (meal.protein || 0), 0);
        const carbs = todaysMeals.reduce((sum, meal) => sum + (meal.carbs || 0), 0);
        const fat = todaysMeals.reduce((sum, meal) => sum + (meal.fat || 0), 0);

        // SINGLE SOURCE OF TRUTH: derive consumed from macros (Atwater factors)
        // Falls back to meal.calories only if macros are all zero (legacy data)
        const macroCalories = Math.round((protein * 4) + (carbs * 4) + (fat * 9));
        const rawCalories = todaysMeals.reduce((sum, meal) => sum + (meal.calories || 0), 0);
        const consumed = (protein > 0 || carbs > 0 || fat > 0) ? macroCalories : rawCalories;

        // Daily target: user's goal + calorie bank (exercise burn shown separately, NOT added)
        let adjustedTarget = userProfile.dailyCalorieTarget + calorieBank;

        // Use centralized safety constant - NEVER allow targets below minimum
        if (adjustedTarget < MIN_SAFE_CALORIES) {
            adjustedTarget = MIN_SAFE_CALORIES;
        }

        // Remaining = goal - eaten (simple, correct math)
        const remaining = Math.max(0, adjustedTarget - consumed);
        // Guard against division by zero
        const percentUsed = adjustedTarget > 0 ? Math.min(100, (consumed / adjustedTarget) * 100) : 0;

        return {
            consumed,
            burned,
            remaining,
            adjustedTarget,
            percentUsed,
            protein,
            carbs,
            fat,
            waterPercent: Math.min(100, (waterIntake / userProfile.waterGoal) * 100),
            stepsPercent: Math.min(100, (stepsCount / userProfile.stepsGoal) * 100),
            calorieBank,
            minSafeIntake: MIN_SAFE_CALORIES
        };
    }, [todaysMeals, todaysExercises, userProfile, waterIntake, stepsCount, calorieBank]);

    const bankCalories = (amount) => {
        setCalorieBank(prev => prev + amount);
    };

    // ===== MEAL OPERATIONS (Supabase First) =====
    const logMeal = async (meal) => {
        // 1. Try Supabase
        const result = await syncMealToSupabase(meal);

        // 2. Prepare meal object (if sync failed, mark pending)
        const mealToStore = {
            ...meal,
            pendingSync: !result.success
        };

        // 3. Update local state (Optimistic + Fallback)
        setTodaysMeals(prev => [...prev, mealToStore]);
    };

    const editMeal = async (id, updatedMeal) => {
        // 1. Try Supabase (Async for edit, optimistic UI)
        // For edits, we usually update UI immediately for responsiveness
        setTodaysMeals(prev => prev.map(meal =>
            meal.id === id ? { ...meal, ...updatedMeal } : meal
        ));

        const result = await updateMealInSupabase(id, updatedMeal);
        if (!result.success) {
            console.warn('[LogContext] Edit meal sync failed, marking pending');
            // Allow background sync service to pick it up later (if implemented)
            // ideally we mark the item as 'dirty' in local state
            setTodaysMeals(prev => prev.map(meal =>
                meal.id === id ? { ...meal, pendingSync: true } : meal
            ));
        }
    };

    const deleteMeal = async (id) => {
        // Optimistic delete
        setTodaysMeals(prev => prev.filter(meal => meal.id !== id));

        const result = await deleteMealFromSupabase(id);
        if (!result.success) {
            console.warn('[LogContext] Delete meal sync failed');
            // In a perfect world we would queue this deletion
        }
    };

    // ===== EXERCISE OPERATIONS (Supabase First) =====
    const logExercise = async (exercise) => {
        // 1. Try Supabase
        const result = await syncWorkoutToSupabase(exercise);

        const exerciseToStore = {
            ...exercise,
            pendingSync: !result.success
        };

        setTodaysExercises(prev => [...prev, exerciseToStore]);
    };

    const editExercise = async (id, updatedExercise) => {
        setTodaysExercises(prev => prev.map(ex =>
            ex.id === id ? { ...ex, ...updatedExercise } : ex
        ));

        const result = await updateWorkoutInSupabase(id, updatedExercise);
        if (!result.success) {
            setTodaysExercises(prev => prev.map(ex =>
                ex.id === id ? { ...ex, pendingSync: true } : ex
            ));
        }
    };

    const deleteExercise = async (id) => {
        setTodaysExercises(prev => prev.filter(ex => ex.id !== id));

        await deleteWorkoutFromSupabase(id);
    };

    // ===== WATER & STEPS =====
    const logWater = (amount) => {
        setWaterIntake(prev => prev + amount);
    };

    const logSteps = (steps) => {
        setStepsCount(prev => prev + steps);
    };

    const value = {
        todaysMeals,
        setTodaysMeals,
        todaysExercises,
        setTodaysExercises,
        waterIntake,
        setWaterIntake,
        stepsCount,
        setStepsCount,
        dailyStreak,
        setDailyStreak,
        reminders,
        setReminders,
        dailySummary,
        logMeal,
        editMeal,
        deleteMeal,
        logExercise,
        editExercise,
        deleteExercise,
        logWater,
        logSteps,
        bankCalories
    };

    return (
        <LogContext.Provider value={value}>
            {children}
        </LogContext.Provider>
    );
};
